import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { 
  FiClipboard, FiShoppingCart, FiMessageCircle, FiUsers, FiBarChart, FiUser, FiSend
} from 'react-icons/fi';
import { getBuyerAssignedRequisitions } from '../../../models/requisitionModel';
import { getAllActiveUsers } from '../../../models/userModel';
import { colors } from '../../../utils/colors';

function BuyerDashboard({ user, dashboardData }) {
  // Helper function to format date
  const formatAssignedDate = (dateValue) => {
    if (dateValue === null || dateValue === undefined || dateValue === '' || dateValue === 0) return 'Not set';
    
    try {
      // Handle Unix timestamp (if it's a number or numeric string)
      const timestamp = typeof dateValue === 'string' ? parseInt(dateValue, 10) : dateValue;
      const date = typeof timestamp === 'number' 
        ? new Date(timestamp * 1000) 
        : new Date(dateValue);
      
      if (isNaN(date.getTime())) return 'Invalid date';
      
      return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
      });
    } catch (error) {
      return 'Invalid date';
    }
  };

  const [myAssignedRequisitions, setMyAssignedRequisitions] = useState([]);
  const [loadingAssigned, setLoadingAssigned] = useState(false);
  const [buyers, setBuyers] = useState([]);
  const [selectedBuyerId, setSelectedBuyerId] = useState(user?.user_id);
  const [loadingBuyers, setLoadingBuyers] = useState(false);

  // Fetch all buyers
  useEffect(() => {
    const fetchBuyers = async () => {
      try {
        setLoadingBuyers(true);
        const response = await getAllActiveUsers();
        
        // Handle different response structures
        const allUsers = response?.rows || response?.data?.rows || response?.data || response || [];
 
        
        // Filter for buyers (role 6) - check multiple possible field names
        const buyersList = allUsers.filter(u => {
          const role = u.user_role || u.role || u.role_id;
          return role === 6 || role === '6';
        });
         
        setBuyers(buyersList);
      } catch (error) { 
        setBuyers([]);
      } finally {
        setLoadingBuyers(false);
      }
    };

    fetchBuyers();
  }, []);

  // Fetch buyer's assigned requisitions
  useEffect(() => {
    const fetchMyAssignedRequisitions = async () => {
      if (!selectedBuyerId) return;
      
      try {
        setLoadingAssigned(true);
        // Use the dedicated endpoint that fetches requisitions for a specific buyer
        const response = await getBuyerAssignedRequisitions(selectedBuyerId);
        
        if (response.status === 1 && response.data) {
          const requisitions = response.data.data || [];
          console.log(`Fetched ${requisitions.length} requisitions for buyer ${selectedBuyerId}`);
          setMyAssignedRequisitions(requisitions);
        }
      } catch (error) {
        console.error('Error fetching assigned requisitions:', error);
        setMyAssignedRequisitions([]);
      } finally {
        setLoadingAssigned(false);
      }
    };

    fetchMyAssignedRequisitions();
  }, [selectedBuyerId]);

  return (
    <div className="space-y-4">
      {/* Header */}
      <div style={{background: 'var(--gradient-primary)'}} className="rounded-xl p-4 text-white shadow-lg">
        <div className="flex flex-col lg:flex-row items-start lg:items-center gap-4">
          {/* Title Section */}
          <div className="flex-1">
            <h1 className="text-2xl font-bold mb-1 text-white">Buyer Dashboard</h1>
            <p className="text-white opacity-90 text-sm">Manage your assigned requisitions and procurement tasks</p>
          </div>

          {/* User Info */}
          <div className="hidden xl:block">
            <div className="bg-white bg-opacity-10 rounded-lg p-3 backdrop-blur-sm min-w-[160px] h-[68px] flex flex-col justify-center">
              <div className="text-white text-xs opacity-75 leading-tight">Welcome back,</div>
              <div className="text-white font-semibold leading-tight mt-0.5">{user?.user_full_name}</div>
            </div>
          </div>
          
          {/* Stats Cards */}
          <div className="flex flex-col sm:flex-row gap-3">
            <Link 
              to="/dashboard/requisitions?status=my_assigned"
              className="bg-white bg-opacity-10 backdrop-blur-sm rounded-lg p-3 hover:bg-opacity-20 transition-all duration-200 border border-white border-opacity-20 min-w-[220px] h-[68px] flex items-center"
            >
              <div className="flex items-center gap-3 w-full">
                <div 
                  className="rounded-full p-2.5 flex items-center justify-center"
                  style={{ backgroundColor: colors.pictonBlue }}
                >
                  <FiShoppingCart className="text-white text-xl" />
                </div>
                <div className="flex-1">
                  <div className="text-white text-xs opacity-75 leading-tight">There are</div>
                  <div className="text-white font-bold leading-tight mt-0.5 flex items-baseline gap-1.5">
                    <span className="text-2xl">{dashboardData?.assigned_requisitions_count || 0}</span>
                    <span className="text-sm">Assigned Requisitions</span>
                  </div>
                </div>
              </div>
            </Link>
            
            <Link 
              to="/dashboard/rfqs?status=ready_to_send"
              className="bg-white bg-opacity-10 backdrop-blur-sm rounded-lg p-3 hover:bg-opacity-20 transition-all duration-200 border border-white border-opacity-20 min-w-[220px] h-[68px] flex items-center"
            >
              <div className="flex items-center gap-3 w-full">
                <div 
                  className="rounded-full p-2.5 flex items-center justify-center"
                  style={{ backgroundColor: colors.pictonBlue }}
                >
                  <FiSend className="text-white text-xl" />
                </div>
                <div className="flex-1">
                  <div className="text-white text-xs opacity-75 leading-tight">There are</div>
                  <div className="text-white font-bold leading-tight mt-0.5 flex items-baseline gap-1.5">
                    <span className="text-2xl">{dashboardData?.rfq_ready_count || 0}</span>
                    <span className="text-sm">RFQs Ready to Send</span>
                  </div>
                </div>
              </div>
            </Link>
          </div> 
     
        </div>
      </div>

      {/* Two Column Layout */}
      <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
        {/* Pending Assignment */}
        <div className="bg-white rounded-xl shadow-lg border" style={{borderColor: 'var(--border-light)'}}>
          <div className="px-2 py-1 border-b bg-gradient-to-r from-blue-50 to-white" style={{borderColor: 'var(--border-light)'}}>
            <div className="flex items-baseline justify-between pt-1 pb-0">
              <div className="flex items-baseline gap-2">
                <div className="w-6 h-6 rounded-md flex items-center justify-center mt-0.5" style={{backgroundColor: '#e3f2fd'}}>
                  <FiClipboard className="w-3.5 h-3.5" style={{color: '#0086D4'}} />
                </div>
                <div>
                  <h3 className="text-sm font-semibold leading-tight" style={{color: 'var(--text-primary)'}}>
                    Pending Assignment
                  </h3>
                  <p className="text-[10px] text-gray-500 leading-tight">
                    {dashboardData.pending_assignment ? dashboardData.pending_assignment.length : 0} pending
                  </p>
                </div>
              </div>
              <Link
                to="/procurement"
                className="text-[11px] font-semibold hover:underline px-2 py-0.5 rounded-md hover:bg-gray-100 transition-colors"
                style={{color: 'var(--color-primary)'}}>
                View All →
              </Link>
            </div>
          </div>
          <div className="divide-y" style={{borderColor: 'var(--border-light)'}}>
            {!dashboardData.pending_assignment || dashboardData.pending_assignment.length === 0 ? (
              <div className="p-8 text-center">
                <FiClipboard className="w-12 h-12 mx-auto mb-3" style={{color: 'var(--color-primary)', opacity: 0.3}} />
                <p className="text-sm font-medium" style={{color: 'var(--text-secondary)'}}>No pending assignments</p>
              </div>
            ) : (
              <div className="max-h-[600px] overflow-y-auto">
                <table className="min-w-full text-xs">
                  <thead>
                    <tr className="bg-gray-50 text-gray-700">
                      <th className="font-semibold px-3 py-2 text-left">Number</th>
                      <th className="font-semibold px-3 py-2 text-left">Department</th>
                      <th className="font-semibold px-3 py-2 text-left">Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {dashboardData.pending_assignment.map((req, index) => (
                      <tr
                        key={index}
                        className="border-b transition hover:bg-blue-50"
                      >
                        <td className="px-3 py-2 font-bold text-primary">
                          EPR-{req.requisition_number}
                        </td>
                        <td className="px-3 py-2">
                          {req.department_name}
                        </td>
                        <td className="px-3 py-2">
                          <Link 
                            to={`/dashboard/requisitions/${req.requisition_id}`} 
                            className="px-2 py-1 text-xs rounded hover:opacity-80 transition-opacity inline-block" 
                            style={{backgroundColor: 'var(--color-primary)', color: 'white'}}
                          >
                            View
                          </Link>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>

        {/* My Assigned Requisitions */}
        <div className="bg-white rounded-xl shadow-lg border" style={{borderColor: 'var(--border-light)'}}>
          <div className="px-2 py-1 border-b bg-gradient-to-r from-green-50 to-white" style={{borderColor: 'var(--border-light)'}}>
            <div className="flex items-baseline justify-between pt-1 pb-0">
              <div className="flex items-baseline gap-2">
                <div className="w-6 h-6 rounded-md flex items-center justify-center mt-0.5" style={{backgroundColor: '#d4edda'}}>
                  <FiShoppingCart className="w-3.5 h-3.5" style={{color: '#28a745'}} />
                </div>
                <div>
                  <h3 className="text-sm font-semibold leading-tight" style={{color: 'var(--text-primary)'}}>
                    Assigned Requisitions
                  </h3>
                  <p className="text-[10px] text-gray-500 leading-tight">
                    {myAssignedRequisitions.length} assigned to you
                  </p>
                </div>
              </div>
              <Link
                to="/dashboard/requisitions?status=my_assigned"
                className="text-[11px] font-semibold hover:underline px-2 py-0.5 rounded-md hover:bg-gray-100 transition-colors"
                style={{color: 'var(--color-primary)'}}>
                View All →
              </Link>
            </div>
          </div>
          {/* Buyer Selector */}
          <div className="px-3 py-2 border-b bg-gray-50" style={{borderColor: 'var(--border-light)'}}>
            <div className="flex items-center gap-2">
              <FiUser className="w-4 h-4 text-gray-500" />
              <label className="text-xs font-medium text-gray-700">View Requisitions For:</label>
              <select
                value={selectedBuyerId}
                onChange={(e) => setSelectedBuyerId(e.target.value)}
                disabled={loadingBuyers}
                className="flex-1 text-xs border rounded px-2 py-1 focus:outline-none focus:ring-2 transition-all"
                style={{
                  borderColor: 'var(--border-light)',
                  focusRingColor: 'var(--color-primary)'
                }}
              >
                {loadingBuyers ? (
                  <option>Loading buyers...</option>
                ) : (
                  <>
                    <option value={user?.user_id}>Me ({user?.user_full_name})</option>
                    {buyers
                      .filter(buyer => buyer.user_id !== user?.user_id)
                      .map(buyer => (
                        <option key={buyer.user_id} value={buyer.user_id}>
                          {buyer.user_full_name}
                        </option>
                      ))}
                  </>
                )}
              </select>
            </div>
          </div>
          <div className="divide-y" style={{borderColor: 'var(--border-light)'}}>
            {loadingAssigned ? (
              <div className="p-8 text-center">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 mx-auto mb-3" style={{borderColor: 'var(--color-primary)'}}></div>
                <p className="text-sm" style={{color: 'var(--text-secondary)'}}>Loading requisitions...</p>
              </div>
            ) : myAssignedRequisitions.length === 0 ? (
              <div className="p-8 text-center">
                <FiShoppingCart className="w-12 h-12 mx-auto mb-3" style={{color: 'var(--color-success)', opacity: 0.3}} />
                <p className="text-sm font-medium" style={{color: 'var(--text-secondary)'}}>
                  {selectedBuyerId === user?.user_id 
                    ? 'No assigned requisitions' 
                    : `No requisitions assigned to ${buyers.find(b => b.user_id == selectedBuyerId)?.user_full_name || 'this buyer'}`
                  }
                </p>
              </div>
            ) : (
              <div className="max-h-[600px] overflow-y-auto">
                <table className="min-w-full text-xs">
                  <thead>
                    <tr className="bg-gray-50 text-gray-700">
                      <th className="font-semibold px-3 py-2 text-left">Number</th>
                      <th className="font-semibold px-3 py-2 text-left">Project</th>
                      <th className="font-semibold px-3 py-2 text-left">Department</th>
                      <th className="font-semibold px-3 py-2 text-left">Assigned</th>
                      <th className="font-semibold px-3 py-2 text-left">Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {myAssignedRequisitions.map((req, index) => (
                      <tr
                        key={index}
                        className="border-b transition hover:bg-green-50"
                      >
                        <td className="px-3 py-2 font-bold text-primary">
                          EPR-{req.requisition_number}
                        </td>
                        <td className="px-3 py-2">
                          {req.specification_title || 'Untitled Project'}
                        </td>
                        <td className="px-3 py-2">
                          {req.department_name}
                        </td>
                        <td className="px-3 py-2 text-sm text-gray-600">
                          {formatAssignedDate(req.requisition_assigned || req.requisition_assignment_date || req.assignment_date || req.assigned_date)}
                        </td>
                        <td className="px-3 py-2">
                          <Link 
                            to={`/dashboard/requisitions/${req.requisition_id}`} 
                            className="px-2 py-1 text-xs rounded hover:opacity-80 transition-opacity inline-block" 
                            style={{backgroundColor: 'var(--color-success)', color: 'white'}}
                          >
                            Manage
                          </Link>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      </div>
 
    </div>
  );
}

export default BuyerDashboard;
